import { useLazyQuery } from '@apollo/client';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  InputProps,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {
  ImagePlugin,
  MaterialDraft,
  MediaPlugin,
  NotificationTypeEnum,
  OneMedia,
  PageWithDrawer,
  getRequiredErrorMessage,
  objectsAreDifferents,
  useConcurrentialAccess,
  useNotification,
  useUnsavedWork,
} from '@prismamedia/one-components';
import {
  GetCategoriesQuery_categories_Category,
  GetMainCategoryAiQuery,
  GetMainCategoryAiQueryVariables,
  GetRecipeAdviceAiQuery,
  GetRecipeAdviceAiQueryVariables,
  GetRecipeCompleteAiQuery,
  GetRecipeCompleteAiQueryVariables,
  GetRecipeLeadAiQuery,
  GetRecipeLeadAiQueryVariables,
  GetRecipeQuery_recipe_Recipe,
  SuggestTagsQuery,
  SuggestTagsQueryVariables,
} from '__generated__/queries-recipe';
import { BrandKey } from '__generated__/queries-web';
import {
  useCreateRecipe,
  useUpdateRecipe,
  useUpdateRecipeLockerId,
} from 'apollo/mutations/recipes.recipe.graphql';
import { useGetImageSourceCategories } from 'apollo/queries/imageSourceCategories.web.graphql';
import { GET_RECIPE_LEAD_IA } from 'apollo/queries/recipeLeadAi.recipe.graphql';
import { useGetRecipe } from 'apollo/queries/recipes.recipe.graphql';
import { MediaInput } from 'components/MediaInput';
import { MultimediaPlugin } from 'components/MediaInput/MultimediaPlugin';
import { TextFieldWordCount } from 'components/TextFieldWordCount';
import { NEW_RECIPE_ID, getEmptyRecipe } from 'const/recipe';
import {
  ChangeEvent,
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { paths, replaceParams } from 'routing';
import { theme } from 'theme';
import { auth } from 'utils/auth';
import { draftActions } from 'utils/draftActions';
import { getStatus } from 'utils/recipeStatus';
import { embedImage, useUploadImage } from 'utils/useUploadImage';
import { DetailsAppBar } from './DetailsAppBar';
import { InformationsDrawer } from './InformationsDrawer';
import { Ingredients } from './Ingredients';
import { useStyles } from './styles';
import {
  getRecipeCreateInput,
  getRecipeUpdateInput,
  getUnlockLink,
  NEW_RECIPE_CATEGORY_ID,
} from './utils';
import { GET_RECIPE_ADVICE_IA } from 'apollo/queries/recipeAdviceAi.recipe.graphql';
import { CheckCircle, Close } from '@mui/icons-material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { GET_RECIPE_COMPLETE_IA } from 'apollo/queries/recipeCompleteAi.recipe.graphql';
import { SUGGEST_TAGS } from 'apollo/queries/suggestTags.recipe.graphql';
import { GET_MAIN_CATEGORY_AI } from 'apollo/queries/recipeMainCategoryAi.recipe.graphql';
import ReactGA from 'react-ga4';

export const RecipeDetailsPage: FC = () => {
  const classes = useStyles();
  const location = useLocation();
  const history = useHistory();
  const uploadImage = useUploadImage();
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const newRecipe = location.pathname.endsWith('/new');
  const [, newBrandKey] = location.pathname.split('/');
  const [recipe, setRecipe] = useState<
    GetRecipeQuery_recipe_Recipe | undefined
  >(undefined);
  const { id: recipeId } = useParams<{ id: string }>();
  const { data, loading, error } = useGetRecipe(recipeId, newRecipe);
  const [getRecipeLeadAi, { loading: loadingRecipeLeadAi }] = useLazyQuery<
    GetRecipeLeadAiQuery,
    GetRecipeLeadAiQueryVariables
  >(GET_RECIPE_LEAD_IA, {
    notifyOnNetworkStatusChange: true,
  });
  const [getRecipeAdviceAi, { loading: loadingRecipeAdviceAi }] = useLazyQuery<
    GetRecipeAdviceAiQuery,
    GetRecipeAdviceAiQueryVariables
  >(GET_RECIPE_ADVICE_IA);

  const [suggestTags, { loading: loadingSuggestTags }] = useLazyQuery<
    SuggestTagsQuery,
    SuggestTagsQueryVariables
  >(SUGGEST_TAGS, {
    fetchPolicy: 'network-only',
    onError: () => {
      pushNotification({
        message: 'Une erreur est survenue lors de la suggestion de tags',
        type: NotificationTypeEnum.error,
      });
    },
    onCompleted: (suggestTagsData) => {
      if (suggestTagsData?.suggestTags?.tagList) {
        setRecipe((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            recipeTags: JSON.parse(
              suggestTagsData?.suggestTags?.tagList as any,
            ).tags.map((tag: { id: string; title: string }, index: number) => ({
              id: `NEW_RECIPE_TAG_ID_${index}`,
              tag: {
                id: tag.id,
                title: tag.title,
              },
            })),
          };
        });
        setIsModalOpen(false);
      } else {
        pushNotification({
          message: 'Une erreur est survenue lors de la suggestion de tags',
          type: NotificationTypeEnum.error,
        });
      }
    },
  });

  const [
    getRecipeCompleteAi,
    { loading: loadingRecipeCompleteAi },
  ] = useLazyQuery<GetRecipeCompleteAiQuery, GetRecipeCompleteAiQueryVariables>(
    GET_RECIPE_COMPLETE_IA,
    {
      fetchPolicy: 'network-only',
      onError: () => {
        pushNotification({
          message:
            'Une erreur est survenue lors de la génération de la recette',
          type: NotificationTypeEnum.error,
        });
      },
      onCompleted: (generatedRecipeData) => {
        if (generatedRecipeData.getRecipeCompleteAi?.recipe) {
          const parsedRecipe = JSON.parse(
            generatedRecipeData.getRecipeCompleteAi?.recipe as any,
          ) as any;
          setRecipe((prev) => ({
            ...emptyRecipe,
            ...parsedRecipe,
            aiGenerated: JSON.stringify(
              prev?.aiGenerated &&
                JSON.parse(prev.aiGenerated).includes('complete')
                ? JSON.parse(prev.aiGenerated)
                : [...JSON.parse(prev?.aiGenerated || '[]'), 'complete'],
            ),
            advice: JSON.stringify(parsedRecipe.advice),
            instructions: JSON.stringify(parsedRecipe.instructions),
            rawIngredients: JSON.stringify(parsedRecipe.rawIngredients),
            lead: JSON.stringify({
              blocks: [
                {
                  key: '8lbug',
                  text: parsedRecipe.lead,
                  type: 'unstyled',
                  depth: 0,
                  inlineStyleRanges: [],
                  entityRanges: [],
                  data: {},
                },
              ],
              entityMap: {},
            }),
          }));
          suggestTags({
            variables: {
              recipe: generatedRecipeData.getRecipeCompleteAi?.recipe,
            },
          });
          getMainCategoryAi({
            variables: {
              recipe: generatedRecipeData.getRecipeCompleteAi?.recipe,
              brandKey: newBrandKey as any,
            },
          });
        } else {
          pushNotification({
            message:
              'Une erreur est survenue lors de la génération de la recette',
            type: NotificationTypeEnum.error,
          });
        }
      },
    },
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [generationType, setGenerationType] = useState('default');
  const [systemPrompt, setSystemPrompt] = useState(
    `Le ton général doit rester neutre, clair et factuel. N'utilise jamais de tutoiement, pas de hashtags, pas de listes de mots isolés, pas de formulations répétitives.`,
  );
  const [recipePrompt, setRecipePrompt] = useState('');
  const [saveLoading, setSaveLoading] = useState(false);
  const updateRecipe = useUpdateRecipe();
  const createRecipe = useCreateRecipe();
  const updateLockerId = useUpdateRecipeLockerId();
  const { pushNotification } = useNotification();
  const emptyRecipe = useMemo(() => getEmptyRecipe(newBrandKey), [newBrandKey]);
  const recipeHasChanged = objectsAreDifferents(
    recipe,
    newRecipe ? emptyRecipe : data?.recipe,
  );
  const getImageSourceCategories = useGetImageSourceCategories();
  const requiredFields = getRequiredErrorMessage(
    recipe,
    getStatus(recipe?.status)?.mandatoryFields,
  );

  const handleMainCategoryChange = useCallback(
    (category: GetCategoriesQuery_categories_Category | null) => {
      setRecipe((prev) => {
        if (!prev) return prev;
        const prevRecipeCategory = prev.recipeCategories.find(
          (c) => c.position === 1,
        );

        return {
          ...prev,
          recipeCategories: [
            ...prev.recipeCategories.filter((cat) => cat.position !== 1),
            {
              ...(prevRecipeCategory || {
                position: 1,
                id: NEW_RECIPE_CATEGORY_ID,
              }),
              category,
            },
          ],
        };
      });
    },
    [setRecipe],
  );

  const [
    getMainCategoryAi,
    { loading: loadingGetMainCategoryAi },
  ] = useLazyQuery<GetMainCategoryAiQuery, GetMainCategoryAiQueryVariables>(
    GET_MAIN_CATEGORY_AI,
    {
      fetchPolicy: 'network-only',
      onError: () => {
        pushNotification({
          message: 'Une erreur est survenue lors de la suggestion de tags',
          type: NotificationTypeEnum.error,
        });
      },
      onCompleted: (mainCategoryAiData) => {
        if (mainCategoryAiData?.getMainCategoryAI?.category) {
          handleMainCategoryChange(
            mainCategoryAiData.getMainCategoryAI
              .category as GetCategoriesQuery_categories_Category,
          );
        } else {
          pushNotification({
            message: 'Une erreur est survenue lors de la suggestion de tags',
            type: NotificationTypeEnum.error,
          });
        }
      },
    },
  );

  const { clearUnsavedWork } = useUnsavedWork(
    newRecipe ? `${NEW_RECIPE_ID}_${newBrandKey}` : recipeId,
    recipe,
    recipeHasChanged,
    setRecipe,
  );

  useConcurrentialAccess({
    lockStatus: newRecipe ? undefined : recipe,
    unlockTimeout: recipeHasChanged ? 1800000 : 300000,
    currentUser: auth.user,
    onLock: (isInitialLock) =>
      updateLockerId(recipeId, auth?.user?.id || null, !isInitialLock),
    unlockLink: getUnlockLink(recipeId),
    clearUnsavedWork,
    onNavigateBack: () => history.push(paths.HOME),
    disableLockForV0: true,
  });

  useEffect(() => {
    setRecipe(newRecipe ? emptyRecipe : data?.recipe || undefined);

    if (data?.recipe?.title) {
      // eslint-disable-next-line immutable/no-mutation
      document.title = data.recipe.title;
    }
  }, [newRecipe, data, emptyRecipe, setRecipe]);

  const setParsedIngredients = useCallback(
    (parsedIngredients) => {
      setRecipe((prev) => (prev ? { ...prev, parsedIngredients } : prev));
    },
    [setRecipe],
  );

  const onSave = async () => {
    if (recipe) {
      setSaveLoading(true);
      if (newRecipe) {
        const res = await createRecipe(getRecipeCreateInput(recipe));
        if (res.data) {
          history.replace(
            replaceParams(paths.DETAILS, {
              brandKey: newBrandKey,
              id: res.data?.createRecipe.id,
            }),
          );
        }
      } else {
        const res = await updateRecipe(recipe.id, getRecipeUpdateInput(recipe));
        pushNotification({
          message: `${res.data?.updateRecipe?.title} a été sauvegardé`,
          type: NotificationTypeEnum.success,
        });
      }
      clearUnsavedWork();
      setSaveLoading(false);
    }
  };

  const getMediasFromString = useCallback(
    (mediasAsString: string | null | undefined) => {
      if (!mediasAsString) return [];
      try {
        return JSON.parse(mediasAsString) as OneMedia[];
      } catch (e) {
        return [];
      }
    },
    [],
  );

  const medias = useMemo(() => {
    return getMediasFromString(recipe?.medias);
  }, [recipe?.medias, getMediasFromString]);

  const onMediaChange: Dispatch<SetStateAction<OneMedia[]>> = useCallback(
    (updater) => {
      setRecipe((prev) => {
        const newMedias =
          typeof updater === 'function'
            ? updater(getMediasFromString(prev?.medias))
            : updater;
        return prev ? { ...prev, medias: JSON.stringify(newMedias) } : prev;
      });
    },
    [getMediasFromString],
  );

  const generateLead = async () => {
    if (!recipe?.title) {
      return pushNotification({
        message: 'Veuillez renseigner le titre de la recette',
        type: NotificationTypeEnum.error,
      });
    }
    const { data: generatedLeadData } = await getRecipeLeadAi({
      variables: {
        title: recipe.title,
        advice: recipe?.advice,
        instructions: recipe?.instructions,
        rawIngredients: recipe?.rawIngredients,
        difficulty: recipe?.difficulty,
        prepTime: recipe?.prepTime,
        waitTime: recipe?.waitTime,
        cookTime: recipe?.cookTime,
        estimatedCost: recipe?.estimatedCost,
        calorieLevel: recipe?.calorieLevel,
      },
    });
    if (generatedLeadData?.getRecipeLeadAi?.lead) {
      setRecipe((prev) => {
        if (prev) {
          return {
            ...prev,
            aiGenerated: JSON.stringify(
              prev.aiGenerated && JSON.parse(prev.aiGenerated).includes('lead')
                ? JSON.parse(prev.aiGenerated)
                : [...JSON.parse(prev.aiGenerated || '[]'), 'lead'],
            ),
            lead: JSON.stringify({
              blocks: [
                {
                  key: '8lbug',
                  text: generatedLeadData?.getRecipeLeadAi?.lead,
                  type: 'unstyled',
                  depth: 0,
                  inlineStyleRanges: [],
                  entityRanges: [],
                  data: {},
                },
              ],
              entityMap: {},
            }),
          };
        }
        return prev;
      });
    }
  };

  const generateAdvice = async () => {
    if (!recipe?.title) {
      return pushNotification({
        message: 'Veuillez renseigner le titre de la recette',
        type: NotificationTypeEnum.error,
      });
    }
    const { data: generatedAdviceData } = await getRecipeAdviceAi({
      variables: {
        title: recipe.title,
        advice: recipe?.advice,
        instructions: recipe?.instructions,
        rawIngredients: recipe?.rawIngredients,
        difficulty: recipe?.difficulty,
        prepTime: recipe?.prepTime,
        waitTime: recipe?.waitTime,
        cookTime: recipe?.cookTime,
        estimatedCost: recipe?.estimatedCost,
        calorieLevel: recipe?.calorieLevel,
      },
    });
    if (generatedAdviceData?.getRecipeAdviceAi?.advice) {
      setRecipe((prev) => {
        if (prev) {
          return {
            ...prev,
            advice: generatedAdviceData?.getRecipeAdviceAi?.advice,
            aiGenerated: JSON.stringify(
              prev.aiGenerated &&
                JSON.parse(prev.aiGenerated).includes('advice')
                ? JSON.parse(prev.aiGenerated)
                : [...JSON.parse(prev.aiGenerated || '[]'), 'advice'],
            ),
          };
        }
        return prev;
      });
    }
  };

  return (
    <>
      <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <Box
          sx={{
            p: 2,
            backgroundColor: 'white',
            width: 800,
            padding: 3,
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            borderRadius: 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 4,
            }}
          >
            <Typography variant="h6">
              <AutoFixHighIcon /> Génération de recette avec IA
            </Typography>
            <IconButton onClick={() => setIsModalOpen(false)}>
              <Close />
            </IconButton>
          </Box>

          {loadingSuggestTags || loadingRecipeCompleteAi ? (
            <Box
              sx={{
                pb: 6,
              }}
            >
              <Typography variant="body1" sx={{ mb: 4 }}>
                Génération de la recette en cours...
              </Typography>
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}
              >
                <Typography variant="body1">
                  1. Création de la recette
                </Typography>
                {'  '}
                {loadingRecipeCompleteAi ? (
                  <CircularProgress size={20} />
                ) : (
                  <CheckCircle />
                )}
              </Box>
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}
              >
                <Typography
                  sx={{
                    color: loadingRecipeCompleteAi
                      ? 'text.secondary'
                      : 'inherit',
                    fontStyle: loadingRecipeCompleteAi ? 'italic' : 'normal',
                  }}
                  variant="body1"
                >
                  2. Ajout des tags
                </Typography>
                {'  '}
                {loadingSuggestTags ? (
                  <CircularProgress size={20} />
                ) : loadingRecipeCompleteAi ? (
                  <Box
                    sx={{
                      width: '20px',
                    }}
                  ></Box>
                ) : (
                  <CheckCircle />
                )}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  sx={{
                    color: loadingRecipeCompleteAi
                      ? 'text.secondary'
                      : 'inherit',
                    fontStyle: loadingRecipeCompleteAi ? 'italic' : 'normal',
                  }}
                  variant="body1"
                >
                  3. Choix de la catégorie principale
                </Typography>
                {'  '}
                {loadingGetMainCategoryAi ? (
                  <CircularProgress size={20} />
                ) : loadingRecipeCompleteAi ? (
                  <Box
                    sx={{
                      width: '20px',
                    }}
                  ></Box>
                ) : (
                  <CheckCircle />
                )}
              </Box>
            </Box>
          ) : (
            <>
              <FormControl sx={{ mb: 4, minWidth: '250px' }}>
                <InputLabel>Type de génération</InputLabel>
                <Select
                  label="Type de génération"
                  value={generationType}
                  onChange={(e) => setGenerationType(e.target.value)}
                >
                  <MenuItem value="default">Recette (par défaut)</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                multiline
                rows={4}
                label="Instructions IA"
                sx={{ mb: 4 }}
                value={systemPrompt}
                onChange={(e) => setSystemPrompt(e.target.value)}
              />

              <TextField
                fullWidth
                multiline
                rows={4}
                label="Instructions Recette"
                sx={{ mb: 4 }}
                placeholder="Demande (mots-clés, recette souhaitée)"
                value={recipePrompt}
                onChange={(e) => setRecipePrompt(e.target.value)}
              />

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => setIsModalOpen(false)}
                >
                  Annuler
                </Button>
                <Button
                  variant="contained"
                  onClick={async () => {
                    if (!systemPrompt || !recipePrompt) {
                      return pushNotification({
                        message:
                          'Veuillez renseigner des instructions pour votre recette',
                        type: NotificationTypeEnum.error,
                      });
                    }
                    ReactGA.event({
                      category: 'Recipe IA Complete',
                      action: 'Recipe IA Generation',
                    });
                    await getRecipeCompleteAi({
                      variables: {
                        systemPrompt,
                        recipePrompt,
                      },
                    });
                  }}
                >
                  Valider
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Modal>
      <DetailsAppBar
        recipe={newRecipe ? recipe : data?.recipe || undefined}
        recipeHasChanged={recipeHasChanged || newRecipe}
        requiredMessage={requiredFields}
        saveRecipe={onSave}
        saveLoading={saveLoading}
        unlock={() =>
          !newRecipe && updateLockerId(recipeId, null).catch(() => {})
        }
        setIsModalOpen={setIsModalOpen}
      />

      <PageWithDrawer
        loading={loading}
        error={
          error ||
          (!recipe && !data?.recipe
            ? Error("Cette recette n'existe pas")
            : undefined)
        }
        rightDrawer={
          <InformationsDrawer recipe={recipe} setRecipe={setRecipe} />
        }
        paddingTop={!smallScreen || requiredFields ? 48 : 0}
      >
        {recipe && (
          <>
            <TextFieldWordCount
              label="Titre"
              variant="standard"
              value={recipe.title}
              onChange={(event: ChangeEvent<{ value: string }>) => {
                const { value } = event.target;
                setRecipe((prev) =>
                  prev ? { ...prev, title: value } : undefined,
                );
              }}
              inputProps={{
                'data-testid': 'detail-title',
              }}
              required
            />

            <MediaInput
              sx={{ mt: 4 }}
              medias={medias}
              setMedias={onMediaChange}
              brandKey={recipe?.brandKey as BrandKey}
              withMediaUploaders
            />

            <MaterialDraft
              className={classes.field}
              shrink={true}
              label={
                <Box sx={{ display: 'flex', gap: 3 }}>
                  <Typography>Chapô de la recette</Typography>
                  {loadingRecipeLeadAi ? (
                    <CircularProgress
                      size={20}
                      sx={{
                        color: '#E91E63',
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        cursor: 'pointer',
                        pb: 0.5,
                        gap: 1,
                      }}
                      display={'flex'}
                      onClick={() => {
                        generateLead();
                      }}
                    >
                      <AutoFixHighIcon
                        sx={{
                          color: '#E91E63',
                        }}
                      />
                      <Box
                        onClick={() => {
                          ReactGA.event({
                            category: 'Recipe IA Lead',
                            action: 'Recipe IA Generation',
                          });
                        }}
                        sx={{
                          background: 'linear-gradient(90deg,#E91E63,#C2185B);',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          backgroundClip: 'text',
                          color: 'transparent',
                        }}
                      >
                        Générer avec IA
                      </Box>
                    </Box>
                  )}
                </Box>
              }
              value={recipe.lead}
              onChange={(lead) => {
                setRecipe((prev) => (prev ? { ...prev, lead } : prev));
              }}
              wordCounter
              actions={[
                draftActions.BOLD,
                draftActions.ITALIC,
                draftActions.LINK,
                draftActions.INTERNAL_LINK,
              ]}
            />

            <TextField
              label="Nombre de personnes"
              variant="standard"
              inputProps={{
                'data-testid': 'detail-person',
              }}
              required
              value={recipe.yield || ''}
              type="number"
              className={classes.field}
              onChange={(event: ChangeEvent<{ value: string }>) => {
                const { value } = event.target;
                if (value && Number(value) > 0) {
                  setRecipe((prev) =>
                    prev ? { ...prev, yield: Number(value) } : undefined,
                  );
                }
              }}
            />

            <Ingredients
              ingredients={recipe.rawIngredients}
              setIngredients={(rawIngredients) => {
                setRecipe((prev) =>
                  prev ? { ...prev, rawIngredients } : prev,
                );
              }}
              setParsedIngredients={setParsedIngredients}
              className={classes.field}
            />

            <MaterialDraft
              className={classes.field}
              label="Étapes de la recette"
              value={recipe.instructions}
              inputProps={
                {
                  'data-testid': 'detail-instructions',
                } as InputProps
              }
              required
              onChange={(instructions) => {
                setRecipe((prev) => (prev ? { ...prev, instructions } : prev));
              }}
              wordCounter
              actions={[
                draftActions.BOLD,
                draftActions.ITALIC,
                draftActions.STRIKETHROUGH,
                draftActions.LINK,
                draftActions.INTERNAL_LINK,
                draftActions.SEPARATOR,
                draftActions.TITLE,
                draftActions.HEADLINE,
                draftActions.LIST,
                draftActions.ORDERED_LIST,
                draftActions.BLOCKQUOTE,
              ]}
              plugins={[
                MediaPlugin({ handleEmbed: embedImage }),
                ImagePlugin({
                  uploadImage: (file) =>
                    uploadImage(file, recipe.brandKey.toLowerCase()),
                  fetchSources: getImageSourceCategories,
                }),
                MultimediaPlugin,
              ]}
            />

            <MaterialDraft
              className={classes.field}
              shrink={true}
              label={
                <Box sx={{ display: 'flex', gap: 3 }}>
                  <Typography>Conseils</Typography>
                  {loadingRecipeAdviceAi ? (
                    <CircularProgress
                      size={20}
                      sx={{
                        color: '#E91E63',
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        cursor: 'pointer',
                        pb: 0.5,
                        gap: 1,
                      }}
                      display={'flex'}
                      onClick={() => {
                        generateAdvice();
                      }}
                    >
                      <AutoFixHighIcon
                        sx={{
                          color: '#E91E63',
                        }}
                      />
                      <Box
                        onClick={() => {
                          ReactGA.event({
                            category: 'Recipe IA Advice',
                            action: 'Recipe IA Generation',
                          });
                        }}
                        sx={{
                          background: 'linear-gradient(90deg,#E91E63,#C2185B);',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          backgroundClip: 'text',
                          color: 'transparent',
                        }}
                      >
                        Générer avec IA
                      </Box>
                    </Box>
                  )}
                </Box>
              }
              value={recipe.advice}
              onChange={(advice) => {
                setRecipe((prev) => (prev ? { ...prev, advice } : prev));
              }}
              wordCounter
              actions={[
                draftActions.BOLD,
                draftActions.ITALIC,
                draftActions.STRIKETHROUGH,
                draftActions.LINK,
                draftActions.INTERNAL_LINK,
                draftActions.SEPARATOR,
                draftActions.HEADLINE,
                draftActions.LIST,
                draftActions.ORDERED_LIST,
                draftActions.BLOCKQUOTE,
              ]}
              plugins={[
                MediaPlugin({ handleEmbed: embedImage }),
                MultimediaPlugin,
              ]}
            />
          </>
        )}
      </PageWithDrawer>
    </>
  );
};
